import React from 'react';
import { type Block } from "@/sections/SectionRenderer";
import { VideoPlayer } from "@/sections/VideoEmbed";
import { ArrowRightIcon } from '@heroicons/react/24/outline';

interface VideoBannerData extends Block {
  url: string;            // YouTube URL (replaces image)
  title?: string;
  description?: string;
  tryHref?: string;
  buttonText?: string;    // ← add
}

export const VideoBanner: React.FC<{ data: VideoBannerData }> = ({ data }) => {
  const { id, url, description, title, tryHref, buttonText } = data; // ← add buttonText

  const imageShadowClass = 'shadow-lg drop-shadow-pinkblue dark:drop-shadow-purpleright';

  // Turn description paragraphs into bullet items and bold the text before the dash
  const bullets = React.useMemo(() => {
    if (!description) return [];
    return description
      .split(/\n\s*\n/) // split on blank lines
      .map(s => s.trim())
      .filter(Boolean);
  }, [description]);

  return (
    <section className="bg-card" id={id}>
      <div className="z-10 font-light mx-4 lg:mx-32 sm:px-6 lg:px-8 py-12 lg:py-16 flex flex-col lg:flex-row items-start lg:items-center">
        <div className="z-0 w-full lg:w-1/2 mb-8 lg:mb-0">
          <VideoPlayer url={url} title={title} className={`rounded-lg shadow-xl ${imageShadowClass}`} />
          {/* (moved button out of the left column) */}
        </div>

        <div className="z-10 px-2 sm:px-0 w-full lg:w-1/2 lg:pl-12 lg:self-center">
          {bullets.length > 0 && (
            <ul className="text-primary text-lg md:text-xl leading-8 mb-6 space-y-5">
              {bullets.map((item, idx) => {
                const dashIndex = item.search(/[—–]/);
                const prefix = dashIndex >= 0 ? item.slice(0, dashIndex).trim() : undefined;
                const rest = dashIndex >= 0 ? item.slice(dashIndex + 1).trim() : item;
                return (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="mt-[0.55em] h-2.5 w-2.5 shrink-0 rounded-full bg-gradient-to-r from-[var(--color-gradient-start)] to-[var(--color-gradient-end)]" />
                    <div className="leading-relaxed">
                      {prefix ? (<><strong className="font-semibold">{prefix}</strong> — {rest}</>) : rest}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {/* TRY IT NOW button under the text (right column), content-width */}
          {tryHref && (
            <div className="mt-4 flex justify-end">
              <a
                href={tryHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-md bg-meteor-shower px-5 py-3 font-bold text-custom-white shadow-lg hover:bg-venetian-night focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-meteor-shower transition-all duration-200 hover:shadow-xl"
              >
                {buttonText ?? 'TRY IT NOW'}
                <ArrowRightIcon className="ml-1 w-5 h-5" />
              </a>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};